import { translate } from '@teamleader/i18n';
import cx from 'classnames';
import debounce from 'lodash/debounce';
import React, { useCallback, useContext, useLayoutEffect, useMemo, useRef } from 'react';

import { useIsSmall } from '../../../../Components/Context/Hooks/mediaQueries';
import useLogoPosition from '../../../../Components/Context/Hooks/useLogoPosition';
import Box from '../../../../Components/UI/Box';
import Branding from '../../../../Components/UI/Branding';
import Button from '../../../../Components/UI/Button';
import OverflowingFlexBox from '../../../../Components/UI/OverflowingFlexBox';
import Pdf from '../../../../Components/UI/Pdf';
import { CUSTOMER_PORTAL_STATUS, QUOTATION_STATUS } from '../../../../Types/quotation';
import { MENUS } from '../../../../Utils/Menus';
import { EVENTS, track } from '../../../../Utils/analytics';
import { canOpenNavigationForDeal } from '../../../../Utils/canOpenNavigation';
import useGlobalContext, { DealContext } from '../../Context';
import { SkeletonPdf } from '../../LoadingSkeleton';
import FeedbackMenu from '../../Menu/Deals/FeedbackMenu';
import SignMenu from '../../Menu/Deals/SignMenu';
import NotFound from '../../NotFound';
import * as styles from '../../styles.css';

interface DealDetailProps {
  loading: boolean;
  defaultLanguage?: string | null;
}

const DealDetail: React.FC<DealDetailProps> = ({ loading, defaultLanguage }) => {
  const { pdf, deal, quotation } = useContext(DealContext);
  const { openMenu, handleOpenMenu, type } = useGlobalContext();
  const isSmall = useIsSmall();
  const { rightOfPdf, belowPdf } = useLogoPosition(canOpenNavigationForDeal(deal));
  const scrollRef = useRef<HTMLDivElement>(null);
  const scrollPosition = useRef(0);
  const quotationId = useRef(quotation?.id);

  useLayoutEffect(() => {
    const container = scrollRef.current;
    if (!container) {
      return;
    }

    const storeScrollLocation = debounce(() => {
      scrollPosition.current = container.scrollTop;
    }, 50);

    container.addEventListener('scroll', storeScrollLocation);
    return () => container.removeEventListener('scroll', storeScrollLocation);
  }, []);

  useLayoutEffect(() => {
    if (quotation?.id === quotationId.current) {
      return;
    }

    quotationId.current = quotation?.id;
    scrollPosition.current = 0;
  }, [quotation]);

  const handleRenderPdf = useCallback(() => {
    scrollRef.current!.scrollTop = scrollPosition.current;
  }, []);

  const isExpired = quotation?.status === QUOTATION_STATUS.EXPIRED;
  const isRefused = quotation?.status === QUOTATION_STATUS.REFUSED;
  const lockedAfterExpiry = quotation?.expiry?.lockedAfterExpiry;
  const isAlreadySigned = useMemo(
    () => deal?.quotations.some((quotation) => quotation.customerPortalStatus === CUSTOMER_PORTAL_STATUS.SIGNED),
    [deal],
  );
  const shouldDisableSign = !pdf || isAlreadySigned || (isExpired && lockedAfterExpiry) || isRefused;

  const handleSign = useCallback(() => {
    handleOpenMenu!(MENUS.SIGN);
    track(EVENTS.STARTED_SIGNING, { fromHeader: false });
  }, [handleOpenMenu]);

  return (
    <>
      {(pdf || loading) && (
        <OverflowingFlexBox ref={scrollRef}>
          <Box className={styles['pdf-holder']}>
            {!pdf && <SkeletonPdf small={isSmall} />}
            <Pdf pdf={pdf} onPdfRender={handleRenderPdf}>
              {isSmall && (
                <Button
                  className={styles['big-button']}
                  onClick={handleSign}
                  disabled={shouldDisableSign}
                  level="primary"
                >
                  {translate('actions.sign')}
                </Button>
              )}
            </Pdf>
            {belowPdf && (
              <Branding
                className={cx(styles['bottom-branding'], {
                  [styles['bottom-branding--mobile']]: isSmall,
                })}
                language={defaultLanguage}
                country={deal?.client.country}
                type={type}
              />
            )}
          </Box>
          {rightOfPdf && (
            <Branding
              className={styles['right-branding']}
              language={defaultLanguage}
              country={deal?.client.country}
              type={type}
            />
          )}
        </OverflowingFlexBox>
      )}

      {!pdf && !loading && <NotFound />}
      {openMenu?.menu === MENUS.SIGN && <SignMenu />}
      {openMenu?.menu === MENUS.FEEDBACK && <FeedbackMenu />}
      {openMenu?.menu === MENUS.REQUEST && <FeedbackMenu isRequestNewQuotation />}
      {openMenu?.menu === MENUS.REFUSE && <FeedbackMenu isRefuseQuotation />}
    </>
  );
};

export default DealDetail;
